import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Menu, X, ArrowUpRight, MapPin, Phone, Mail, ChevronDown } from "lucide-react";
import { useCompany } from "@/lib/content-context";

const NAV_LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/products", label: "Products", testid: "nav-products-link" },
  { to: "/board", label: "Board", testid: "nav-board-link" },
];

const INVESTOR_LINKS = [
  { to: "/investor-relations/financial-statements", label: "Financial Statements", testid: "nav-financial-statements-link" },
  { to: "/investor-relations/shareholder-information", label: "Shareholder Information", testid: "nav-shareholder-information-link" },
];

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [irOpen, setIrOpen] = useState(false);
  const location = useLocation();
  const company = useCompany();
  const closeTimer = useRef(null);
  const irRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Click outside closes the IR dropdown.
  useEffect(() => {
    if (!irOpen) return;
    const onDocClick = (e) => {
      if (irRef.current && !irRef.current.contains(e.target)) setIrOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [irOpen]);

  // Escape closes the IR dropdown.
  useEffect(() => {
    if (!irOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setIrOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [irOpen]);

  const irHover = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setIrOpen(true);
  };
  const irLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIrOpen(false), 180);
  };

  const closeMenu = () => setOpen(false);
  const isIrActive = location.pathname.startsWith("/investor-relations");

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-xl ${scrolled ? "bg-[#F8F7F4]/90 border-b border-[#1A1A1A]/10 shadow-sm" : "bg-[#F8F7F4]/70 border-b border-[#1A1A1A]/5"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
        <Link to="/" data-testid="brand-wordmark" className="flex items-end gap-3 leading-none">
          <div className="flex flex-col">
            <span className="font-serif-display text-2xl tracking-[0.18em] uppercase">GESL</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5A56] mt-0.5">Spinners Limited</span>
          </div>
          <span className="hidden sm:inline text-[9px] tracking-wider text-[#5C5A56]/70 italic pb-0.5">
            (formerly known as {company.formerName})
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map(l => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid}
              className={({ isActive }) => `text-sm tracking-wide link-underline ${isActive ? "text-[#1A1A1A] font-medium" : "text-[#5C5A56]"}`}>
              {l.label}
            </NavLink>
          ))}

          <div ref={irRef} className="relative" onMouseEnter={irHover} onMouseLeave={irLeave}>
            <button data-testid="nav-investor-relations-link" onClick={() => setIrOpen(o => !o)}
              className={`text-sm tracking-wide inline-flex items-center gap-1 link-underline ${isIrActive ? "text-[#1A1A1A] font-medium" : "text-[#5C5A56]"}`}>
              Investor Relations <ChevronDown size={14} className={`transition-transform ${irOpen ? "rotate-180" : ""}`} />
            </button>
            {irOpen && (
              <>
                {/* Invisible bridge so the cursor can travel from button to menu without losing hover */}
                <div aria-hidden className="absolute right-0 top-full h-3 w-72" />
                <div
                  data-testid="investor-dropdown"
                  className="absolute right-0 top-full mt-3 w-72 bg-[#F8F7F4] border border-[#1A1A1A]/10 shadow-xl py-2"
                  onMouseEnter={irHover}
                  onMouseLeave={irLeave}
                >
                  {INVESTOR_LINKS.map(l => (
                    <NavLink key={l.to} to={l.to} data-testid={l.testid} onClick={() => setIrOpen(false)}
                      className={({ isActive }) => `block px-5 py-3 text-sm border-l-2 transition-colors ${isActive ? "border-[#C25934] bg-[#EAE6DF] text-[#1A1A1A] font-medium" : "border-transparent text-[#5C5A56] hover:bg-[#EAE6DF]/60 hover:text-[#1A1A1A]"}`}>
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </div>
        </nav>

        <Link to="/contact" data-testid="nav-inquire-button" className="hidden lg:inline-flex btn-primary text-sm">
          Inquire Now <ArrowUpRight size={16} />
        </Link>

        <button data-testid="nav-mobile-toggle" className="lg:hidden p-2" onClick={() => setOpen(o => !o)} aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F8F7F4] border-t border-[#1A1A1A]/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map(l => (
              <NavLink key={l.to} to={l.to} onClick={closeMenu} data-testid={`mobile-${l.testid}`}
                className={({ isActive }) => `text-base ${isActive ? "text-[#1A1A1A] font-medium" : "text-[#5C5A56]"}`}>
                {l.label}
              </NavLink>
            ))}
            <div className="pt-2 mt-2 border-t border-[#1A1A1A]/10">
              <div className="overline mb-3">Investor Relations</div>
              {INVESTOR_LINKS.map(l => (
                <NavLink key={l.to} to={l.to} onClick={closeMenu} data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) => `block py-2 text-base ${isActive ? "text-[#1A1A1A] font-medium" : "text-[#5C5A56]"}`}>
                  {l.label}
                </NavLink>
              ))}
            </div>
            <Link to="/contact" onClick={closeMenu} data-testid="mobile-nav-inquire-button" className="btn-primary text-sm w-fit mt-2">
              Inquire Now <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const company = useCompany();
  return (
    <footer data-testid="site-footer" className="bg-[#1A1A1A] text-[#F8F7F4] mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-serif-display text-4xl tracking-[0.12em] uppercase">GESL</div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#D9D4CC] mt-2">Spinners Limited</div>
            <p className="mt-6 text-sm text-[#D9D4CC] max-w-md leading-relaxed">
              A modern ring-spinning unit producing recycled polyester dope dyed spun yarns and
              speciality yarns across a wide count range, engineered for global textile partners.
            </p>
            <div className="mt-6 font-mono-ed text-[11px] tracking-widest text-[#D9D4CC]/70">
              CIN · {company.cin}
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-5">Explore</div>
            <ul className="space-y-3 text-sm text-[#D9D4CC]">
              {NAV_LINKS.map(l => (
                <li key={l.to}><Link to={l.to} data-testid={`footer-${l.testid}`} className="link-underline">{l.label}</Link></li>
              ))}
              {INVESTOR_LINKS.map(l => (
                <li key={l.to}><Link to={l.to} data-testid={`footer-${l.testid}`} className="link-underline">{l.label}</Link></li>
              ))}
              <li><Link to="/contact" data-testid="footer-contact-link" className="link-underline">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline mb-5">Visit / Contact</div>
            <div className="text-sm text-[#D9D4CC] space-y-4">
              <div className="flex gap-3">
                <MapPin size={16} className="mt-0.5 shrink-0 text-[#C25934]" />
                <span>{company.addressLines.map((l, i) => <span key={i}>{l}<br/></span>)}</span>
              </div>
              <div className="flex gap-3 items-center">
                <Phone size={16} className="text-[#C25934]" />
                <a href={`tel:${company.phoneRaw}`} className="link-underline" data-testid="footer-phone">{company.phone}</a>
              </div>
              <div className="flex gap-3 items-center">
                <Mail size={16} className="text-[#C25934]" />
                <a href={`mailto:${company.email}`} className="link-underline" data-testid="footer-email">{company.email}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="divider-line bg-[#F8F7F4]/15 mt-16" />
        <div className="mt-6 flex flex-col md:flex-row justify-between text-xs text-[#D9D4CC]/70 gap-3">
          <span>© {new Date().getFullYear()} {company.name}. All rights reserved.</span>
          <span className="font-mono-ed tracking-widest">Formerly known as {company.formerName}</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <>
      <Nav />
      <main className="pt-24">{children}</main>
      <Footer />
    </>
  );
}
